import React, { useCallback, useEffect, useRef, useState } from "react";
import { Swiper, SwiperSlide } from "swiper/react";
import {
  Autoplay,
  FreeMode,
  Navigation,
  Pagination,
  Thumbs,
  Zoom,
} from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "swiper/css/zoom";
import {
  getDocumentById,
  getDocumentByOpeAndRefAndDocCatAndExcludeFile,
  getDocumentByOpeAndRefAndDocCatDownscaled,
  getDownscaledFile,
  getFile,
} from "../api/documentUpload";
import NoImage from "../assets/no-image.png";
import NoThumbnail from "../assets/no-thumbnail.jpg";
import LoadingSpinner from "./LoadingSpinner";

const types = {
  DRIVER: "driver",
  VEHICLE: "vehicle",
};

// Gallery component
function Gallery({ activeTab, driverId, vehicleId }) {
  const [selectedImage, setSelectedImage] = useState(null);
  const [driverDocImageMap, setDriverDocImageMap] = useState({});
  const [driverDocThumbnailMap, setDriverDocThumbnailMap] = useState([]);
  const [vehicleDocThumbnailMap, setVehicleDocThumbnailMap] = useState([]);
  const [vehicleDocImageMap, setVehicleDocImageMap] = useState([]);

  const [isOpen, setIsOpen] = useState(false);
  const [isLoadingDriver, setIsLoadingDriver] = useState(false);
  const [fetchingImage, setFetchingImage] = useState(false);
  const [isLoadingVehicle, setIsLoadingVehicle] = useState(false);

  useEffect(() => {
    (async () => {
      if (driverId) {
        const driverImages = [];
        setIsLoadingDriver(true);
        const drivers = await getDocumentByOpeAndRefAndDocCatAndExcludeFile(
          9,
          driverId,
          1,
        );
        for (let driver of drivers) {
          let fileData = null;

          try {
            fileData = await getDownscaledFile(driver.docId);
          } catch (error) {
            console.error("Error fetching file:", error);
          }

          let file = null;
          if (fileData) {
            file = new File([fileData], driver.docName, {
              type: fileData.type,
            });
          }

          driverImages.push({
            docId: driver.docId,
            file: file === null ? NoThumbnail : URL.createObjectURL(file),
          });
        }

        if (driverImages?.length > 0) {
          const docId = driverImages[0].docId;
          if (driverDocImageMap[docId]) {
            setSelectedImage(driverDocImageMap[docId]);
          } else {
            const image = await getImage(activeTab, docId);
            setDriverDocImageMap((prevState) => ({
              ...prevState,
              [docId]: image,
            }));
            setSelectedImage(image);
          }
        } else {
          setSelectedImage(NoImage);
        }

        setDriverDocThumbnailMap(driverImages);
        setIsLoadingDriver(false);
      }
    })();
  }, [driverId]);

  useEffect(() => {
    (async () => {
      if (vehicleId) {
        const vehicleImages = [];
        setIsLoadingVehicle(true);
        const vehicles = await getDocumentByOpeAndRefAndDocCatDownscaled(
          4,
          vehicleId,
          1,
        );
        for (let vehicle of vehicles) {
          let fileData = null;
          try {
            fileData = await getDownscaledFile(vehicle.docId);
          } catch (error) {
            console.error("Error fetching file:", error);
          }
          let file = null;

          if (fileData) {
            file = new File([fileData], vehicle.docName, {
              type: fileData.type,
            });
          }

          vehicleImages.push({
            docId: vehicle.docId,
            file: file === null ? NoThumbnail : URL.createObjectURL(file),
          });
        }

        if (vehicleImages?.length > 0) {
          const docId = vehicleImages[0].docId;
          if (vehicleDocImageMap[docId]) {
            setSelectedImage(vehicleDocImageMap[docId]);
          } else {
            const image = await getImage(activeTab, docId);
            setVehicleDocImageMap((prevState) => ({
              ...prevState,
              [docId]: image,
            }));
            setSelectedImage(image);
          }
        } else {
          setSelectedImage(NoImage);
        }

        setVehicleDocThumbnailMap(vehicleImages);
        setIsLoadingVehicle(false);
      }
    })();
  }, [vehicleId]);

  const swiperStyle = {
    display: "flex",
    flexDirection: "column",
  };

  const getImage = useCallback(async (type, docId) => {
    const document = await getDocumentById(docId);

    const fileData = await getFile(document.docId);

    const file = new File([fileData], document.docName, {
      type: fileData.type,
    });

    return file === null ? NoImage : URL.createObjectURL(file);
  }, []);

  useEffect(() => {
    (async () => {
      setFetchingImage(true);
      if (activeTab === types.DRIVER) {
        if (driverDocThumbnailMap.length > 0) {
          const docId = driverDocThumbnailMap[0].docId;
          if (driverDocImageMap[docId]) {
            setSelectedImage(driverDocImageMap[docId]);
          } else {
            const image = await getImage(activeTab, docId);
            setDriverDocImageMap((prevState) => ({
              ...prevState,
              [docId]: image,
            }));
            setSelectedImage(image);
          }
        } else {
          setSelectedImage(NoImage);
        }
      } else {
        if (vehicleDocThumbnailMap.length > 0) {
          const docId = vehicleDocThumbnailMap[0].docId;
          if (vehicleDocImageMap[docId]) {
            setSelectedImage(vehicleDocImageMap[docId]);
          } else {
            const image = await getImage(activeTab, docId);
            setVehicleDocImageMap((prevState) => ({
              ...prevState,
              [docId]: image,
            }));
            setSelectedImage(image);
          }
        } else {
          setSelectedImage(NoImage);
        }
      }
      setFetchingImage(false);
    })();
  }, [activeTab]);

  const handleDriverImageSelect = async (docId) => {
    console.log(driverDocImageMap);
    if (driverDocImageMap[docId]) {
      setSelectedImage(driverDocImageMap[docId]);
    } else {
      setFetchingImage(true);
      const image = await getImage(types.DRIVER, docId);
      setDriverDocImageMap((prevState) => ({
        ...prevState,
        [docId]: image,
      }));
      setSelectedImage(image);
      setFetchingImage(false);
    }
  };

  const handleVehicleImageSelect = async (docId) => {
    if (vehicleDocImageMap[docId]) {
      setSelectedImage(vehicleDocImageMap[docId]);
    } else {
      setFetchingImage(true);
      const image = await getImage(types.VEHICLE, docId);
      setVehicleDocImageMap((prevState) => ({
        ...prevState,
        [docId]: image,
      }));
      setSelectedImage(image);
      setFetchingImage(false);
    }
  };

  return (
    <div
      className="gallery"
      style={{
        display:
          activeTab === "vehicle" || activeTab === "driver" ? "flex" : "none",
        zIndex:
          activeTab === "vehicle" || activeTab === "driver" ? "100" : "-1",
        position:
          activeTab === "vehicle" || activeTab === "driver" ? "absolute" : "",
      }}
    >
      {isOpen && (
        <ImageZoomInOut
          selectedImage={selectedImage}
          onClose={() => setIsOpen(false)}
        />
      )}
      <div className="selected-image-container">
        {fetchingImage ? (
          <LoadingSpinner />
        ) : (
          <>
            {selectedImage && (
              <img
                src={selectedImage}
                alt="Selected"
                onClick={isOpen ? null : () => setIsOpen(true)}
              />
            )}
          </>
        )}
      </div>
      <div className="swp">
        <Swiper
          style={{
            ...swiperStyle,
            "--swiper-navigation-color": "black",
            "--swiper-pagination-color": "white",
          }}
          zoom={true}
          spaceBetween={50}
          navigation={true}
          modules={[FreeMode, Navigation, Thumbs, Autoplay, Pagination, Zoom]} // Include Autoplay module
          autoplay={{
            delay: 3000,
            disableOnInteraction: true, // Continue autoplay after user interaction
          }}
          pagination={{ clickable: true }}
          slidesPerView={4}
          className="mySwiper"
        >
          {activeTab === "driver" && (
            <>
              {isLoadingDriver ? (
                <LoadingSpinner height="20vh" showText={false} />
              ) : (
                <>
                  {driverDocThumbnailMap?.map((image, index) => (
                    <SwiperSlide
                      key={index}
                      onClick={() => handleDriverImageSelect(image.docId)}
                    >
                      <img src={image.file} alt={`Slide ${index}`} />
                    </SwiperSlide>
                  ))}
                </>
              )}
            </>
          )}

          {activeTab === "vehicle" && (
            <>
              {isLoadingVehicle ? (
                <LoadingSpinner height="20vh" showText={false} />
              ) : (
                <>
                  {vehicleDocThumbnailMap?.map((image, index) => (
                    <SwiperSlide
                      key={index}
                      onClick={() => handleVehicleImageSelect(image.docId)}
                    >
                      <img src={image.file} alt={`Slide ${index}`} />
                    </SwiperSlide>
                  ))}
                </>
              )}
            </>
          )}
        </Swiper>
      </div>
    </div>
  );
}

//image zooming function
function ImageZoomInOut({ selectedImage, onClose }) {
  const [scale, setScale] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const imgRef = useRef(null);

  const zoomIn = () => {
    setScale((scale) => scale + 0.1);
  };

  const zoomOut = () => {
    setScale((scale) => scale - 0.1);
  };

  const reset = () => {
    setScale(1);
  };

  useEffect(() => {
    const image = imgRef.current;
    let isDragging = false;
    let prevPosition = { x: 0, y: 0 };

    const handleMouseDown = (e) => {
      isDragging = true;
      prevPosition = { x: e.clientX, y: e.clientY };
    };
    const handleMouseMove = (e) => {
      if (!isDragging) return;
      const deltaX = e.clientX - prevPosition.x;
      const deltaY = e.clientY - prevPosition.y;

      prevPosition = { x: e.clientX, y: e.clientY };
      setPosition((position) => ({
        x: position.x + deltaX,
        y: position.y + deltaY,
      }));
    };

    const handleMouseUp = () => {
      isDragging = false;
    };

    image?.addEventListener("mousedown", handleMouseDown);
    image?.addEventListener("mousemove", handleMouseMove);
    image?.addEventListener("mouseup", handleMouseUp);

    return () => {
      image?.removeEventListener("mousedown", handleMouseDown);
      image?.removeEventListener("mousemove", handleMouseMove);
      image?.removeEventListener("mouseup", handleMouseUp);
    };
  }, [imgRef, scale]);
  return (
    <div className="image-modal">
      <div className="image-modal-content">
        <span className="close" onClick={onClose}>
          &times;
        </span>
        <div className="btn-container">
          <button onClick={zoomIn}>
            <span style={{ fontWeight: "bold" }}>+</span>
          </button>
          <button onClick={zoomOut}>
            <span style={{ fontWeight: "bold" }}>-</span>
          </button>
          <button onClick={reset}>
            <span style={{ fontWeight: "bold" }}>R</span>
          </button>
        </div>
        <img
          ref={imgRef}
          src={selectedImage}
          alt=""
          style={{
            width: "50vw",
            height: "auto",
            cursor: "move",
            transform: `scale(${scale}) translate(${position.x}px,${position.y}px)`,
          }}
          draggable={false}
        />
      </div>
    </div>
  );
}

export default Gallery;
